import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, onSnapshot, updateDoc, deleteDoc, doc, getDocs, query, where, orderBy, serverTimestamp } from 'firebase/firestore';

const LISTS = [
  { id: 'OrderList1', title: 'Einkaufsliste 1' },
  { id: 'OrderList2', title: 'Einkaufsliste 2' },
  { id: 'OrderList3', title: 'Einkaufsliste 3' },
];

export default function OrderLists() {
  const lists = LISTS;
  const [activeListIndex, setActiveListIndex] = useState(0);
  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [photoBase64, setPhotoBase64] = useState('');
  const [photoLoading, setPhotoLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [listTransition, setListTransition] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  useEffect(() => {
    setItems([]);
    setEditingId(null);
    setEditingName('');
    // Order by client-side timestamp for immediate, stable ordering when items are added locally.
    // We still write `createdAt` (serverTimestamp) for canonical server time, but serverTimestamp
    // is resolved asynchronously which can cause the list to appear out-of-order immediately after add.
    const q = query(collection(db, lists[activeListIndex].id), orderBy('createdAtLocal', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setItems(data);
    }, (err) => console.error('OrderList onSnapshot', err));
    return () => unsub();
  }, [activeListIndex]);

  const estimateBytesFromDataUrl = (dataUrl) => {
    if (!dataUrl) return 0;
    const base64 = dataUrl.split(',')[1] || '';
    return Math.ceil((base64.length * 3) / 4);
  };

  const compressImageToDataUrl = (file, opts = {}) => new Promise((resolve, reject) => {
    const maxBytes = opts.maxBytes || 800 * 1024;
    const maxDim = opts.maxDim || 640;
    const minDim = opts.minDim || 80;
    const img = new Image();
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('File read failed'));
    reader.onload = () => {
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        let targetW = Math.round(img.width * scale);
        let targetH = Math.round(img.height * scale);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas not supported'));

        let quality = 0.82;
        let dataUrl = '';

        const tryEncode = () => {
          canvas.width = targetW;
          canvas.height = targetH;
          ctx.clearRect(0, 0, targetW, targetH);
          ctx.drawImage(img, 0, 0, targetW, targetH);
          dataUrl = canvas.toDataURL('image/jpeg', quality);
          return estimateBytesFromDataUrl(dataUrl);
        };

        let bytes = tryEncode();
        while (bytes > maxBytes && (quality > 0.5 || Math.max(targetW, targetH) > minDim)) {
          if (quality > 0.5) {
            quality = Math.max(0.5, quality - 0.08);
          } else {
            const nextScale = Math.max(minDim / Math.max(targetW, targetH), 0.85);
            targetW = Math.max(minDim, Math.round(targetW * nextScale));
            targetH = Math.max(minDim, Math.round(targetH * nextScale));
          }
          bytes = tryEncode();
        }

        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('Image decode failed'));
      img.src = String(reader.result);
    };

    reader.readAsDataURL(file);
  });

  const handlePhotoChange = async (file) => {
    if (!file) return;
    setPhotoLoading(true);
    try {
      const dataUrl = await compressImageToDataUrl(file);
      setPhotoBase64(dataUrl);
    } catch (err) {
      console.error('photo compress error', err);
      setPhotoBase64('');
    } finally {
      setPhotoLoading(false);
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    const val = name.trim();
    if (!val) return;
    if (items.length >= 100) {
      window.alert('Die Liste darf maximal 100 Einträge enthalten. Bitte entfernen Sie einen oder mehrere Einträge, um fortzufahren.');
      return;
    }
    setLoading(true);
    try {
      const now = Date.now();
      await addDoc(collection(db, lists[activeListIndex].id), {
        name: val,
        crossed: false,
        createdAt: serverTimestamp(),
        createdAtLocal: now,
        photoBase64: photoBase64 || null,
      });
      setName('');
      setPhotoBase64('');
    } catch (err) {
      console.error('addItem error', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCross = async (it) => {
    try {
      const ref = doc(db, lists[activeListIndex].id, it.id);
      await updateDoc(ref, { crossed: !it.crossed });
    } catch (err) {
      console.error('toggleCross error', err);
    }
  };

  const remove = async (it) => {
    try {
      await deleteDoc(doc(db, lists[activeListIndex].id, it.id));
    } catch (err) {
      console.error('delete order item', err);
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditingName(it.name || '');
  };

  const saveEdit = async (it) => {
    const val = editingName.trim();
    if (!val) return;
    try {
      await updateDoc(doc(db, lists[activeListIndex].id, it.id), { name: val });
      setEditingId(null);
      setEditingName('');
    } catch (err) {
      console.error('saveEdit error', err);
    }
  };

  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  return (
    <div className="orderlists">
      <div className="orderlist-header">
        <button
          className="orderlist-nav-btn"
          type="button"
          onClick={() => {
            const next = (activeListIndex + lists.length - 1) % lists.length;
            setListTransition('slide-right');
            setActiveListIndex(next);
            setName('');
            setPhotoBase64('');
            setSelectedPhoto(null);
            setConfirmDeleteAll(false);
          }}
          aria-label="Vorherige Einkaufsliste"
          title="Vorherige Einkaufsliste"
        >
          ◀
        </button>
        <h2 className="orderlist-title">{lists[activeListIndex].title}</h2>
        <button
          className="orderlist-nav-btn"
          type="button"
          onClick={() => {
            const next = (activeListIndex + 1) % lists.length;
            setListTransition('slide-left');
            setActiveListIndex(next);
            setName('');
            setPhotoBase64('');
            setSelectedPhoto(null);
            setConfirmDeleteAll(false);
          }}
          aria-label="Naechste Einkaufsliste"
          title="Naechste Einkaufsliste"
        >
          ▶
        </button>
      </div>
      <form className="order-form" onSubmit={addItem}>
        <input className="order-input" value={name} onChange={e => setName(e.target.value)} placeholder="Artikelname" />
        <div className="order-form-actions">
          <input
            id="order-photo-gallery-input"
            type="file"
            accept="image/*"
            className="order-photo-input"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              handlePhotoChange(file);
              e.target.value = '';
            }}
            aria-label="Foto aus Galerie hinzufügen"
          />
          <input
            id="order-photo-camera-input"
            type="file"
            accept="image/*"
            capture="environment"
            className="order-photo-input"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              handlePhotoChange(file);
              e.target.value = '';
            }}
            aria-label="Foto mit Kamera aufnehmen"
          />
          <label
            className={`order-photo-btn ${photoLoading ? 'is-loading' : ''}`}
            htmlFor="order-photo-gallery-input"
            title="Foto aus Galerie wählen"
          >
            {photoLoading ? '…' : 'Galerie'}
          </label>
          <label
            className={`order-photo-btn ${photoLoading ? 'is-loading' : ''}`}
            htmlFor="order-photo-camera-input"
            title="Foto mit Kamera aufnehmen"
          >
            {photoLoading ? '…' : 'Kamera'}
          </label>
          <button className="order-add-btn" type="submit" disabled={loading}>{loading ? '…' : 'Hinzufügen'}</button>
          {photoBase64 ? (
            <img className="order-photo-preview" src={photoBase64} alt="Vorschau" />
          ) : null}
        </div>
      </form>

      <ul key={activeListIndex} className={`order-ul ${listTransition}`}>
        {items.map(it => (
          <li key={it.id} className="order-li">
            <div className="order-item-content">
              {it.photoBase64 ? (
                <img
                  className="order-item-photo"
                  src={it.photoBase64}
                  alt="Foto"
                  onClick={() => setSelectedPhoto(it.photoBase64)}
                />
              ) : null}
              {editingId === it.id ? (
                <input
                  className="order-edit-input"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit(it);
                    if (e.key === 'Escape') {
                      setEditingId(null);
                      setEditingName('');
                    }
                  }}
                  autoFocus
                />
              ) : (
                <span className={`order-name ${it.crossed ? 'crossed' : ''}`}>{it.name}</span>
              )}
            </div>
            <div className="order-item-actions">
              <button
                className="order-edit-btn"
                type="button"
                onClick={() => (editingId === it.id ? saveEdit(it) : startEdit(it))}
                title={editingId === it.id ? 'Speichern' : 'Bearbeiten'}
                aria-label={editingId === it.id ? 'Speichern' : 'Bearbeiten'}
              >
                {editingId === it.id ? '💾' : '✎'}
              </button>
              {editingId === it.id ? (
                <button
                  className="order-cancel-btn"
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setEditingName('');
                  }}
                  title="Abbrechen"
                  aria-label="Abbrechen"
                >
                  ↩
                </button>
              ) : null}
              <button className="order-cross-btn" type="button" onClick={() => toggleCross(it)} title={it.crossed ? 'Rückgängig' : 'Abhaken'}>{it.crossed ? '↺' : '✔'}</button>
              <button className="order-delete-btn" type="button" onClick={() => remove(it)} title="Löschen">✖</button>
            </div>
          </li>
        ))}
      </ul>

      <div className="order-delete-all">
        <button className="order-delete-crossed-btn" onClick={() => setConfirmDeleteAll(true)} disabled={deletingAll}>
          {deletingAll ? '…' : 'Alle abgehakten Elemente löschen'}
        </button>
      </div>

      {confirmDeleteAll && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999}}>
          <div style={{background: '#fff', padding: 20, borderRadius: 8, width: 320, maxWidth: '90%'}}>
            <p style={{marginTop: 0}}>Alle abgehakten Elemente wirklich löschen?</p>
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8}}>
              <button onClick={async () => {
                setDeletingAll(true);
                try {
                  const q = query(collection(db, lists[activeListIndex].id), where('crossed', '==', true));
                  const snap = await getDocs(q);
                  const batchDeletes = snap.docs.map(d => deleteDoc(doc(db, 'OrderList', d.id)));
                  await Promise.all(batchDeletes);
                } catch (err) {
                  console.error('deleteAllCrossed error', err);
                } finally {
                  setDeletingAll(false);
                  setConfirmDeleteAll(false);
                }
              }} disabled={deletingAll} style={{background: '#c62828', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4}}>{deletingAll ? '…' : 'Ja, löschen'}</button>
              <button onClick={() => setConfirmDeleteAll(false)} disabled={deletingAll} style={{padding: '6px 12px', borderRadius: 4}}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {selectedPhoto && (
        <div className="order-photo-modal" onClick={() => setSelectedPhoto(null)} role="dialog" aria-modal="true">
          <img className="order-photo-modal-img" src={selectedPhoto} alt="Foto" />
        </div>
      )}
    </div>
  );
}
